// "Track the ISS": the space station as a first-class view. The button at
// the top of the controls panel (and a live button on phones) focuses the
// ISS at true scale on the real clock, and the camera follows it round the
// Earth. While it is the selected body, however it was reached (the button,
// a click on the station, the object browser, a /viewer/#iss link), a card
// under the distance readout says where it is: the sea or the place below,
// how high and how fast, and whether it is in sunlight, with a link to the
// /iss/ pass times and alerts and a way back to where you were.
import { issState } from '../iss/orbit.js';
import { vt } from '../i18n/viewer.js';

const fmt0 = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 });
// The address the page opened with, read before init's focus moves rewrite it.
const OPENED_ON_ISS = typeof window !== 'undefined' && /^#\/?iss$/i.test(window.location.hash);
const latText = (v) => `${Math.abs(v).toFixed(1)}° ${v >= 0 ? 'N' : 'S'}`;
const lonText = (v) => `${Math.abs(v).toFixed(1)}° ${v >= 0 ? 'E' : 'W'}`;

// api: { getIss, getEarth, getSatrec, getSimDate, getSelected, isAnimating,
//        focus, isRealistic, setRealistic, isRealTime, setRealTime }
export function initIssTracking(api) {
  const button = document.getElementById('iss-track-btn');
  const buttonSub = document.getElementById('iss-track-sub');
  const fab = document.getElementById('iss-track-fab');
  const card = document.getElementById('iss-live-card');
  if (!card) return;
  const el = (id) => document.getElementById(id);
  const place = el('iss-live-place');
  const tag = el('iss-live-tag');
  const alt = el('iss-live-alt');
  const speed = el('iss-live-speed');
  const pos = el('iss-live-pos');
  const light = el('iss-live-light');
  const stop = el('iss-live-stop');

  // What the button found when it was pressed, so Stop can put it back.
  let startedFrom = null;
  let shown = false;
  // The place names and the water mask, loaded the first time it is shown.
  let where = null;
  let water = null;
  function loadWhere() {
    if (where) return;
    where = {};
    import('../site/iss/where.js').then((m) => {
      where.whereIs = m.whereIs;
      water = m.waterMask('/marketing/flat-earth/water-2k.webp');
    }).catch(() => {});
  }

  function issSelected() {
    const iss = api.getIss();
    return Boolean(iss && api.getSelected() === iss);
  }

  function setShown(on) {
    if (on === shown) return;
    shown = on;
    card.hidden = !on;
    document.body.classList.toggle('iss-tracking', on);
    if (button) {
      button.setAttribute('aria-pressed', on ? 'true' : 'false');
      button.classList.toggle('is-active', on);
      const title = button.querySelector('.iss-track-title');
      if (title) title.textContent = vt(on ? 'controls.iss.tracking' : 'controls.iss.title');
    }
    if (!on) startedFrom = null;
    else { loadWhere(); update(); }
  }

  function update() {
    const satrec = api.getSatrec();
    if (!satrec) { place.textContent = 'Finding the station…'; return; }
    const st = issState(satrec, api.getSimDate());
    if (!st) return;
    const isWater = water ? water.isWater(st.lat, st.lon) : null;
    place.textContent = where && where.whereIs && isWater !== null
      ? where.whereIs(st.lat, st.lon, isWater).text
      : `Over ${latText(st.lat)}, ${lonText(st.lon)}`;
    alt.textContent = `${fmt0.format(st.altKm)} km up`;
    speed.textContent = `${fmt0.format(st.speedKmh)} km/h`;
    pos.textContent = `${latText(st.lat)}, ${lonText(st.lon)}`;
    light.textContent = st.sunlit ? 'In sunlight' : 'In the Earth’s shadow';
    const live = api.isRealTime();
    tag.textContent = live ? 'Live' : 'Sim time';
    card.classList.toggle('is-live', live);
  }

  function closeMobileMenu() {
    if (document.body.classList.contains('mobile-menu-open')) {
      document.getElementById('mobile-panel-backdrop')?.click();
    }
  }

  // `via` marks a person's press, for the analytics (focusOnObject).
  function track({ fromLink = false, via = null } = {}) {
    const iss = api.getIss();
    if (!iss) return;
    closeMobileMenu();
    if (issSelected()) { api.setRealTime(); update(); return; }
    // A /viewer/#iss visitor came for the station: Stop takes them to the
    // Earth it orbits.
    startedFrom = fromLink ? null : { selected: api.getSelected(), realistic: api.isRealistic() };
    // Where it is now: the real clock, then the station at true scale.
    api.setRealTime();
    api.focus(iss, via);
  }

  function untrack() {
    const from = startedFrom;
    startedFrom = null;
    setShown(false);
    const back = from && from.selected && from.selected !== api.getIss() ? from.selected : api.getEarth();
    if (back) api.focus(back);
    // Back to the scale it was in once the camera has arrived.
    if (from && !from.realistic) {
      const restore = () => {
        if (api.isAnimating()) { setTimeout(restore, 200); return; }
        if (api.isRealistic()) api.setRealistic(false);
      };
      setTimeout(restore, 200);
    }
  }

  if (button) button.addEventListener('click', () => (issSelected() ? untrack() : track({ via: 'iss' })));
  if (fab) fab.addEventListener('click', () => track({ via: 'iss' }));
  if (stop) stop.addEventListener('click', untrack);
  if (buttonSub) buttonSub.textContent = vt('controls.iss.sub');

  // A link to /viewer/#iss: the startup can land on its hero body after the
  // hash's own focus (the ISS's switch to true scale refocuses whatever was
  // selected), so once the opening move has settled, go to the station.
  if (OPENED_ON_ISS) {
    const started = Date.now();
    let settled = 0;
    const settle = () => {
      if (issSelected()) return;
      settled = api.getIss() && !api.isAnimating() ? settled + 1 : 0;
      if (settled >= 3) { track({ fromLink: true }); return; }
      if (Date.now() - started < 30000) setTimeout(settle, 400);
    };
    setTimeout(settle, 1200);
  }

  // For checks from the console or a test.
  window.__issTrack = { track, untrack, get opened() { return OPENED_ON_ISS; }, get selected() { return issSelected(); }, get animating() { return api.isAnimating(); }, get found() { return Boolean(api.getIss()); } };

  // The card follows the selection: on whenever the ISS is the focused body.
  let ticks = 0;
  setInterval(() => {
    setShown(issSelected());
    if (shown && (ticks++ % 2 === 0)) update();
  }, 500);
}
